import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  getConstanceSettings,
  getUser,
} from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'
import { validateEmail } from '@disco/utils/validation'

import { Brief } from 'types/brief'

import {
  Box,
  Button as DsButton,
  Dialog,
  Flex as DsFlex,
  Link,
  Text,
  TextArea,
  TextField,
} from 'components/ds'
import Button from 'components/ui/button'
import InlineError from 'components/ui/inline-error'

import { RecipientInput } from '../recipient-input'
import { BriefCard } from './brief-card'
import { useShareBrief } from './hooks'
import mixpanel from '@disco/api/mixpanel'

/**
 * Props for the ShareBriefModal component
 */
interface ShareBriefModalProps {
  /** Callback function to close the modal */
  onClose?: () => void
  /** Brief information to be shared */
  brief: Brief
  /** Optional array of email addresses to pre-populate the recipient input */
  initialRecipients?: string[]
  /** callback to handle manage access action */
  onManageAccess: () => void
  /** callback to handle reopening a brief */
  handleReopenBrief: () => Promise<void>
  /** Array of all active recipients */
  allRecipients?: string[]
}

/**
 * Data structure for sharing a brief
 */
interface ShareBriefData {
  /** Array of recipient email addresses */
  recipients: string[]
  /** Reply-to email address */
  replyToAddress: string
  /** Sender display name */
  senderName: string
  /** Email subject line */
  subject: string
  /** Email message body */
  message: string
}

/**
 * Recipient tag representation for the UI
 */
interface RecipientTag {
  /** Unique identifier for the recipient tag */
  id: string
  /** Email address of the recipient */
  email: string
  /** Whether the email address is valid */
  isValid: boolean
}

/**
 * ShareBriefModal - A modal component for sharing briefs via email
 *
 * This component provides a comprehensive interface for sharing briefs with multiple recipients.
 * It includes recipient autocomplete, email validation, customizable subject and message,
 * and integration with the DISCO user/business search API.
 *
 * Features:
 * - Multi-recipient email input with autocomplete
 * - Email validation with visual feedback
 * - Pre-populated subject line from brief title
 * - Optional reply-to address configuration
 * - Custom message body
 * - Integration with brief access management
 * - Keyboard navigation and accessibility support
 * - Real-time form validation
 *
 * @param props - Component props
 * @returns ShareBriefModal component
 *
 * @example
 * ```tsx
 * {showModal && (
 *   <ShareBriefModal
 *     onClose={() => setShowModal(false)}
 *     brief={{
 *       id: 123,
 *       name: "My Brief Title",
 *       created: "Dec 20, 2024, 6:51AM"
 *     }}
 *     initialRecipients={["user@example.com"]}
 *   />
 * )}
 * ```
 */
export const ShareBriefModal: React.FC<ShareBriefModalProps> = ({
  onClose,
  brief,
  initialRecipients = [],
  onManageAccess,
  handleReopenBrief,
  allRecipients,
}) => {
  const { shareBrief } = useShareBrief()
  const constanceSettings = useSelector(getConstanceSettings)
  const briefWorkflowV2Enabled = constanceSettings?.BRIEF_WORKFLOW_V2 || false
  const user = useSelector(getUser)

  // State for form fields
  const [recipients, setRecipients] = useState<RecipientTag[]>([])
  const [senderName, setSenderName] = useState('')
  const [replyToAddress, setReplyToAddress] = useState('')
  const [replyToAddressError, setReplyToAddressError] = useState<string | null>(
    null,
  )
  const [isReplyToAddressFocused, setIsReplyToAddressFocused] = useState(false)
  const [subject, setSubject] = useState(brief.name ? `${brief.name}` : '')
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize default values when component mounts
  const [hasInitialized, setHasInitialized] = React.useState(false)

  React.useEffect(() => {
    if (brief && !hasInitialized) {
      setSubject(brief.name || 'Brief')
      setMessage('')
      setError(null)

      // Prefill sender name from brief or user
      const defaultSenderName =
        brief.sender_name ||
        (user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : '')
      setSenderName(defaultSenderName)

      // Prefill reply-to from brief or user email
      const defaultReplyTo = brief.sender_email || user.email || ''
      setReplyToAddress(defaultReplyTo)

      // Initialize recipients from prop
      if (initialRecipients.length > 0) {
        const recipientTags: RecipientTag[] = initialRecipients.map(email => ({
          id: `${Date.now()}-${Math.random()}`,
          email: email.trim(),
          isValid: validateEmail(email.trim()),
        }))
        setRecipients(recipientTags)
      } else {
        setRecipients([])
      }

      setHasInitialized(true)
      mixpanel.sendPureEvent('Brief share modal opened', {
        Context: 'Briefs',
        brief_id: brief.id,
      })
    }
  }, [brief, initialRecipients, hasInitialized, user])

  const addRecipient = useCallback(
    (email: string) => {
      if (!email.trim()) return

      const trimmedEmail = email.trim()
      const isValid = validateEmail(trimmedEmail)

      // Check for duplicates
      const isDuplicate = recipients.some(
        r => r.email.toLowerCase() === trimmedEmail.toLowerCase(),
      )
      if (isDuplicate) {
        return
      }

      const newRecipient: RecipientTag = {
        id: `${Date.now()}-${Math.random()}`,
        email: trimmedEmail,
        isValid,
      }

      setRecipients(prev => [...prev, newRecipient])
    },
    [recipients],
  )

  const removeRecipient = useCallback((id: string) => {
    setRecipients(prev => prev.filter(r => r.id !== id))
  }, [])

  const handleReplyToAddressChange = useCallback((value: string) => {
    setReplyToAddress(value)

    if (value.trim()) {
      const isValid = validateEmail(value.trim())
      setReplyToAddressError(
        isValid ? null : 'Please enter a valid email address',
      )
    } else {
      setReplyToAddressError(null)
    }
  }, [])

  const validRecipients = useMemo(() => {
    return recipients.filter(r => r.isValid)
  }, [recipients])

  // Check if brief is open for V2 workflow
  const isBriefDraft =
    briefWorkflowV2Enabled && brief?.publishing_status === 'draft'

  const isBriefClosed =
    briefWorkflowV2Enabled &&
    brief?.submission_status === 'closed' &&
    brief?.publishing_status !== 'draft'

  const canSend =
    validRecipients.length > 0 &&
    subject.trim() &&
    !replyToAddressError &&
    !isBriefDraft

  const handleSend = useCallback(async () => {
    if (!canSend) return

    setIsLoading(true)
    setError(null)

    if (isBriefClosed) {
      await handleReopenBrief()
      mixpanel.sendPureEvent('Brief reopened', {
        Context: 'Briefs',
        brief_id: brief.id,
      })
    }

    try {
      const shareData: ShareBriefData = {
        recipients: validRecipients.map(r => r.email),
        replyToAddress: replyToAddress.trim(),
        senderName: senderName.trim(),
        subject: subject.trim(),
        message: message.trim(),
      }

      const result = await shareBrief(brief.id, shareData)
      let customizedFields = []
      if (
        subject.trim() !== '' &&
        subject.trim() !== 'Brief' &&
        subject.trim() !== brief.name
      ) {
        customizedFields.push('subject')
      }
      if (message.trim() !== '') {
        customizedFields.push('message')
      }
      const defaultReplyTo = brief.sender_email || user.email || ''
      if (
        replyToAddress.trim() !== '' &&
        replyToAddress.trim() !== defaultReplyTo
      ) {
        customizedFields.push('reply_to')
      }
      if (result.success) {
        mixpanel.sendPureEvent('Brief shared', {
          Context: 'Briefs',
          brief_id: brief.id,
          recipient_count: recipientCount,
          customized_fields: customizedFields,
        })
        // Show success notification
        pushUINotification({
          message: `Brief shared with ${validRecipients.length} recipient${
            validRecipients.length === 1 ? '' : 's'
          }!`,
          level: 'success',
        })

        onClose?.()
      } else {
        throw new Error(result.error || 'Failed to share brief')
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to share brief. Please try again.'
      setError(errorMessage)

      pushUINotification({
        message: 'Failed to share brief. Please try again.',
        level: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    canSend,
    validRecipients,
    replyToAddress,
    senderName,
    subject,
    message,
    brief.id,
    shareBrief,
    onClose,
    handleReopenBrief,
    isBriefClosed,
  ])

  const recipientCount = validRecipients.length
  const sendButtonText =
    recipientCount > 0
      ? `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`
      : 'Send'

  const handleAddAllRecipients = useCallback(() => {
    allRecipients.forEach(email => {
      addRecipient(email)
    })
  }, [allRecipients, addRecipient])

  const allRecipientsButton = (
    <div>
      <DsButton mt="1" ml="2" variant="ghost" onClick={handleAddAllRecipients}>
        <Text size="2" color="blue">
          + Add existing recipients
        </Text>
      </DsButton>
    </div>
  )

  return (
    <Dialog.Root defaultOpen onOpenChange={onClose}>
      <Dialog.Content maxWidth="600px" aria-describedby={undefined}>
        <Dialog.Title>
          <Text size="6" weight="bold" mb="3" as="div">
            Share brief
          </Text>
        </Dialog.Title>
        {/* Header */}
        <Box mb="4">
          {/* Brief Card */}
          <Box mb="4">
            <BriefCard brief={brief} />
          </Box>
        </Box>

        {/* Form Content */}
        <Box>
          {/* Recipients Input */}
          <Box mb="3">
            <RecipientInput
              recipients={recipients}
              onAdd={addRecipient}
              onRemove={removeRecipient}
              aria-label="Brief share recipients"
            />
            {!!allRecipients?.length && allRecipientsButton}
          </Box>

          {/* Reply-to Address */}
          <Box mb="3">
            <Text
              size="2"
              weight="medium"
              mb="2"
              as="label"
              htmlFor="reply-to-address"
            >
              Reply to address
            </Text>
            <TextField.Root
              id="reply-to-address"
              size="2"
              type="email"
              placeholder="you@company.com"
              value={replyToAddress}
              onChange={e => handleReplyToAddressChange(e.target.value)}
              onFocus={() => setIsReplyToAddressFocused(true)}
              onBlur={() => setIsReplyToAddressFocused(false)}
              aria-describedby="reply-to-help"
              color={
                replyToAddressError && !isReplyToAddressFocused
                  ? 'red'
                  : undefined
              }
            />
            {replyToAddressError && !isReplyToAddressFocused && (
              <InlineError error={replyToAddressError} />
            )}
          </Box>

          {/* Sender Display Name */}
          <Box mb="3">
            <Text
              size="2"
              weight="medium"
              mb="2"
              as="label"
              htmlFor="sender-name"
            >
              Sender display name
            </Text>
            <TextField.Root
              id="sender-name"
              size="2"
              type="text"
              placeholder="Your Name"
              value={senderName}
              onChange={e => setSenderName(e.target.value)}
              aria-describedby="sender-name-help"
            />
          </Box>

          {/* Subject */}
          <Box mb="3">
            <Text
              size="2"
              weight="medium"
              mb="2"
              as="label"
              htmlFor="email-subject"
            >
              Subject
            </Text>
            <TextField.Root
              id="email-subject"
              size="2"
              placeholder="Enter email subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              required
              aria-required="true"
            />
          </Box>

          {/* Message */}
          <Box mb="3">
            <Text
              size="2"
              weight="medium"
              mb="2"
              as="label"
              htmlFor="email-message"
            >
              Message
            </Text>
            <TextArea
              id="email-message"
              size="2"
              placeholder="Hi everyone, check out this new brief!"
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={6}
              aria-describedby="message-help"
            />
          </Box>

          {/* Manage Brief Access Link */}
          {(brief.status === 'sent' || brief.sent_status) && (
            <Box mb="4">
              <Link href="#" size="2" color="blue" onClick={onManageAccess}>
                Manage brief access
              </Link>
            </Box>
          )}

          {/* Draft Brief Warning */}
          {isBriefDraft && (
            <Box
              mb="3"
              p="3"
              style={{
                backgroundColor: 'var(--orange-3)',
                borderRadius: '6px',
              }}
            >
              <Text size="2" color="orange" weight="medium">
                This brief is still in draft and cannot be shared. You need to
                publish the brief first to share it.
              </Text>
            </Box>
          )}

          {/* Error Message */}
          {error && (
            <Box mb="3">
              <Text size="2" color="red">
                {error}
              </Text>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <DsFlex gap="3" justify="end">
          <Dialog.Close>
            <DsButton variant="soft" color="gray" size="2">
              Cancel
            </DsButton>
          </Dialog.Close>

          <Button
            appearance="primary"
            onClick={handleSend}
            disabled={!canSend || isLoading}
            caption={sendButtonText}
            progressCaption="Sending"
            progressIndicator={isLoading}
          />
        </DsFlex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
